import Swal from 'sweetalert2';

/**
 * Shared handling for the PDF generation endpoints.
 *
 * Three outcomes, not two. The one that used to be missing is the middle one:
 *
 *   success            — the document is complete.
 *   success + warnings — the document was produced but is missing something. A finding
 *                        left off, a summary checkbox that could not be worked out, a
 *                        diagram that would not load. These used to go to the server log
 *                        and nowhere else, so the person holding a deficient report had
 *                        no idea. They are shown now.
 *   error              — nothing was produced.
 */

export interface PdfResponse {
  status: number;
  result?: {
    message?: string;
    warnings?: string[];
  };
}

function bullets(items: string[]): string {
  return '<ul style="text-align:left;margin:0;padding-left:1.2em;">'
    + items.map(w => '<li>' + escapeHtml(w) + '</li>').join('')
    + '</ul>';
}

function escapeHtml(s: string): string {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Show the outcome of a generate call.
 *
 * @returns true when a document was produced, warnings or not, so callers can still
 *          refresh their view.
 */
export function showPdfResult(response: PdfResponse): boolean {
  const result = response && response.result ? response.result : {};
  const message = result.message || 'Done.';
  const warnings = result.warnings || [];

  if (response && response.status === 200) {
    if (warnings.length) {
      Swal({
        title: 'Generated, with warnings',
        html: escapeHtml(message)
          + '<p style="margin-top:1em;text-align:left;"><b>Please check the document before sending it:</b></p>'
          + bullets(warnings),
        type: 'warning',
      });
    } else {
      Swal('Success!', message, 'success');
    }
    return true;
  }

  Swal('Error!', message, 'error');
  return false;
}

/**
 * Show a transport-level failure — a 500, a timeout, a dropped connection.
 *
 * Without this the request simply vanished: no dialog, and any loading spinner the caller
 * had turned on stayed on.
 */
export function showPdfError(error?: any): void {
  const detail = (error && (error.statusText || error.message)) ? ' (' + (error.statusText || error.message) + ')' : '';
  Swal(
    'Error!',
    'The document could not be generated' + detail + '. It has been logged — please pass the job number to support.',
    'error',
  );
}
